/**
 * QZ Tray Print Service
 * 
 * Handles all direct printing via QZ Tray local service.
 * QZ Tray must be installed and running on the POS machine.
 * 
 * Download QZ Tray: https://qz.io/download/
 * 
 * Features:
 * - Auto-connect/reconnect to QZ Tray
 * - Printer status monitoring
 * - ESC/POS raw printing for thermal printers
 * - Multi-printer support
 * - Certificate signing for production
 * 
 * DEBUG MODE:
 * Set window.DEBUG_PRINT = true in browser console to enable detailed logging
 */

import qz from 'qz-tray';
import { buildReceipt, buildKitchenTicket, buildBill, buildTestPrint } from '../utils/escposBuilder';

// Debug helper
const debugLog = (...args) => {
  if (typeof window !== 'undefined' && window.DEBUG_PRINT) {
    console.log('[QZ-DEBUG]', ...args);
  }
};

// Connection status enum
export const QZStatus = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  ERROR: 'ERROR',
};

// Printer status enum
export const PrinterStatus = {
  UNKNOWN: 'UNKNOWN',
  READY: 'READY',
  BUSY: 'BUSY',
  ERROR: 'ERROR',
  OFFLINE: 'OFFLINE',
};

class QZPrintService {
  constructor() {
    this.status = QZStatus.DISCONNECTED;
    this.printers = [];
    this.lastError = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Set();
    this.isInitialized = false;
    
    // Printer configuration (will be loaded from settings)
    this.printerConfig = {
      receipt: null,
      kitchen: null,
      bar: null,
    };
  }

  // ============ INITIALIZATION & CONNECTION ============

  /**
   * Initialize QZ Tray connection
   */
  async initialize() {
    if (this.isInitialized) {
      return this.status === QZStatus.CONNECTED;
    }

    // Configure QZ Tray
    this._configureCertificates();
    
    // Set up connection listeners
    qz.websocket.setClosedCallbacks((evt) => {
      console.log('[QZ] Connection closed:', evt);
      this._handleDisconnect();
    });

    qz.websocket.setErrorCallbacks((evt) => {
      console.error('[QZ] Connection error:', evt);
      this.lastError = evt?.message || 'Connection error';
      this._updateStatus(QZStatus.ERROR);
    });

    this.isInitialized = true;
    return this.connect();
  }

  /**
   * Configure certificates for QZ Tray
   * 
   * IMPORTANT NOTE on Certificate Signing:
   * - Certificate signing is NOT required for printing to work
   * - It only removes the "Untrusted website" popup in QZ Tray
   * - User can click "Allow" on the popup to proceed with printing
   * - Printing should still work after clicking Allow
   * 
   * For production: Use proper certificate signing from QZ support
   * For development/testing: Use this bypass configuration
   */
  _configureCertificates() {
    debugLog('Configuring certificates...');
    
    // Option 1: Allow unsigned requests (development mode)
    // This will show a popup in QZ Tray but printing will still work
    qz.security.setCertificatePromise((resolve, reject) => {
      // For development - use empty certificate to trigger unsigned mode
      // QZ Tray will show "Untrusted" popup but allow printing
      debugLog('Certificate promise called - returning empty for unsigned mode');
      resolve('');
    });
    
    // For signature - return empty to use unsigned mode
    qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        debugLog('Signature promise called - returning empty for unsigned mode');
        // Empty signature = unsigned mode (will show popup but works)
        resolve('');
      };
    });
    
    debugLog('Certificate configuration complete (unsigned mode)');
  }

  /**
   * Connect to QZ Tray
   */
  async connect() {
    debugLog('connect() called');
    debugLog('qz.websocket.isActive():', qz.websocket.isActive());
    
    if (qz.websocket.isActive()) {
      debugLog('Already connected, returning true');
      this._updateStatus(QZStatus.CONNECTED);
      return true;
    }

    this._updateStatus(QZStatus.CONNECTING);
    
    try {
      debugLog('Attempting qz.websocket.connect()...');
      await qz.websocket.connect();
      console.log('[QZ] Connected successfully');
      debugLog('Connection successful, isActive:', qz.websocket.isActive());
      this._updateStatus(QZStatus.CONNECTED);
      this.reconnectAttempts = 0;
      this.lastError = null;
      
      // Refresh printer list
      await this.refreshPrinters();
      debugLog('Printer list refreshed:', this.printers);
      
      return true;
    } catch (err) {
      console.error('[QZ] Connection failed:', err);
      debugLog('Connection error:', err);
      this.lastError = this._parseError(err);
      this._updateStatus(QZStatus.ERROR);
      return false;
    }
  }

  /**
   * Disconnect from QZ Tray
   */
  async disconnect() {
    if (qz.websocket.isActive()) {
      try {
        await qz.websocket.disconnect();
      } catch (err) {
        console.warn('[QZ] Disconnect error:', err);
      }
    }
    this._updateStatus(QZStatus.DISCONNECTED);
  }

  /**
   * Handle disconnection - attempt reconnect
   */
  async _handleDisconnect() {
    this._updateStatus(QZStatus.DISCONNECTED);
    
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`[QZ] Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
      
      setTimeout(async () => {
        await this.connect();
      }, this.reconnectDelay);
    } else {
      console.error('[QZ] Max reconnect attempts reached');
      this.lastError = 'QZ Tray tidak dapat dihubungi. Pastikan QZ Tray berjalan.';
    }
  }

  // ============ PRINTER MANAGEMENT ============

  /**
   * Get list of available printers
   */
  async getPrinters() {
    if (!qz.websocket.isActive()) {
      return [];
    }

    try {
      const printers = await qz.printers.find();
      this.printers = printers.map(name => ({
        name,
        status: PrinterStatus.UNKNOWN,
      }));
      return this.printers;
    } catch (err) {
      console.error('[QZ] Failed to get printers:', err);
      return [];
    }
  }

  /**
   * Refresh printer list and status
   */
  async refreshPrinters() {
    await this.getPrinters();
    this._notifyListeners();
  }

  /**
   * Get detailed printer status
   */
  async getPrinterStatus(printerName) {
    if (!qz.websocket.isActive() || !printerName) {
      return { status: PrinterStatus.UNKNOWN };
    }

    try {
      // Check if printer exists
      const printers = await qz.printers.find();
      const exists = printers.some(p => p.toLowerCase() === printerName.toLowerCase());
      
      if (!exists) {
        return { status: PrinterStatus.OFFLINE, message: 'Printer tidak ditemukan' };
      }

      // QZ doesn't have direct status API, so we check via a test config
      const config = qz.configs.create(printerName);
      
      return { 
        status: PrinterStatus.READY, 
        message: 'Printer tersedia',
        name: printerName 
      };
    } catch (err) {
      return { 
        status: PrinterStatus.ERROR, 
        message: this._parseError(err) 
      };
    }
  }

  /**
   * Set printer configuration
   */
  setPrinterConfig(role, printerName) {
    if (['receipt', 'kitchen', 'bar'].includes(role)) {
      this.printerConfig[role] = printerName;
      this._notifyListeners();
    }
  }

  /**
   * Get configured printer for role
   */
  getPrinterForRole(role) {
    return this.printerConfig[role];
  }

  /**
   * Load printer config from backend/localStorage
   */
  loadPrinterConfig(config) {
    if (config) {
      this.printerConfig = {
        receipt: config.receipt || config.receipt_printer || null,
        kitchen: config.kitchen || config.kitchen_printer || null,
        bar: config.bar || config.bar_printer || null,
      };
      this._notifyListeners();
    }
  }

  // ============ PRINTING FUNCTIONS ============

  /**
   * Print raw ESC/POS data
   */
  async printRaw(printerName, data, options = {}) {
    debugLog('printRaw() called');
    debugLog('Printer:', printerName);
    debugLog('Data length:', Array.isArray(data) ? data.length : 1);
    debugLog('WebSocket isActive:', qz.websocket.isActive());
    
    if (!qz.websocket.isActive()) {
      const err = new Error('QZ Tray tidak terhubung');
      debugLog('ERROR: QZ not active');
      throw err;
    }

    if (!printerName) {
      const err = new Error('Nama printer tidak ditentukan');
      debugLog('ERROR: No printer name');
      throw err;
    }

    // Verify printer exists
    debugLog('Verifying printer exists...');
    const availablePrinters = await qz.printers.find();
    debugLog('Available printers:', availablePrinters);
    
    const printerExists = availablePrinters.some(
      p => p.toLowerCase() === printerName.toLowerCase()
    );
    
    if (!printerExists) {
      debugLog('ERROR: Printer not found in available list');
      throw new Error(`Printer "${printerName}" tidak ditemukan. Available: ${availablePrinters.join(', ')}`);
    }

    const config = qz.configs.create(printerName, {
      encoding: options.encoding || 'cp437',
    });
    debugLog('Config created:', config);

    const printData = [{
      type: 'raw',
      format: 'command',
      data: Array.isArray(data) ? data : [data],
    }];
    debugLog('Print data prepared, commands count:', printData[0].data.length);

    try {
      debugLog('Calling qz.print()...');
      await qz.print(config, printData);
      console.log(`[QZ] Print success: ${printerName}`);
      debugLog('PRINT SUCCESS');
      return { success: true, printer: printerName };
    } catch (err) {
      console.error(`[QZ] Print failed: ${printerName}`, err);
      debugLog('PRINT FAILED:', err);
      debugLog('Error name:', err.name);
      debugLog('Error message:', err.message);
      debugLog('Error stack:', err.stack);
      throw new Error(this._parseError(err));
    }
  }

  /**
   * Print customer receipt
   * @throws Error if printing fails - NO FALLBACK
   */
  async printReceipt(order, outlet, options = {}) {
    debugLog('printReceipt() called');
    debugLog('Order:', order?.id, order?.order_number);
    debugLog('Outlet:', outlet?.name);
    debugLog('Receipt printer config:', this.printerConfig.receipt);
    
    const printerName = options.printer || this.printerConfig.receipt;
    
    if (!printerName) {
      debugLog('ERROR: No receipt printer configured');
      throw new Error('Printer kasir belum dikonfigurasi');
    }

    if (!qz.websocket.isActive()) {
      debugLog('ERROR: QZ not active');
      throw new Error('QZ Tray tidak aktif. Receipt tidak dapat dicetak.');
    }

    debugLog('Building receipt ESC/POS data...');
    const escposData = buildReceipt(order, outlet, {
      paperWidth: options.paperWidth || 80,
      includeQR: options.includeQR || false,
      baseUrl: options.baseUrl,
    });
    debugLog('ESC/POS data built, commands:', escposData?.length);

    return this.printRaw(printerName, escposData);
  }

  /**
   * Print kitchen ticket
   */
  async printKitchen(order, options = {}) {
    debugLog('printKitchen() called');
    debugLog('Order:', order?.id, order?.order_number);
    debugLog('Kitchen printer config:', this.printerConfig.kitchen);
    
    const printerName = options.printer || this.printerConfig.kitchen;
    
    if (!printerName) {
      console.warn('[QZ] Kitchen printer not configured');
      debugLog('WARNING: Kitchen printer not configured');
      return { success: false, reason: 'Printer dapur belum dikonfigurasi' };
    }

    debugLog('Building kitchen ticket ESC/POS data...');
    const escposData = buildKitchenTicket(order, 'KITCHEN', {
      paperWidth: options.paperWidth || 80,
    });

    if (!escposData) {
      debugLog('No FOOD items to print');
      return { success: true, reason: 'No food items to print' };
    }
    
    debugLog('ESC/POS data built, commands:', escposData?.length);

    try {
      return await this.printRaw(printerName, escposData);
    } catch (err) {
      // Kitchen can have fallback - return error but don't throw
      console.error('[QZ] Kitchen print failed:', err);
      debugLog('Kitchen print FAILED:', err.message);
      return { success: false, error: err.message, canFallback: true };
    }
  }

  /**
   * Print bar ticket
   */
  async printBar(order, options = {}) {
    const printerName = options.printer || this.printerConfig.bar;
    
    if (!printerName) {
      console.warn('[QZ] Bar printer not configured');
      return { success: false, reason: 'Printer bar belum dikonfigurasi' };
    }

    const escposData = buildKitchenTicket(order, 'BAR', {
      paperWidth: options.paperWidth || 80,
    });

    if (!escposData) {
      return { success: true, reason: 'No drink items to print' };
    }

    try {
      return await this.printRaw(printerName, escposData);
    } catch (err) {
      console.error('[QZ] Bar print failed:', err);
      return { success: false, error: err.message, canFallback: true };
    }
  }

  /**
   * Print bill (pre-payment)
   */
  async printBill(order, outlet, options = {}) {
    const printerName = options.printer || this.printerConfig.receipt;
    
    // Bill can use browser fallback if QZ not available
    if (!printerName || !qz.websocket.isActive()) {
      return { success: false, canFallback: true, reason: 'QZ Tray tidak aktif' };
    }

    const escposData = buildBill(order, outlet, {
      paperWidth: options.paperWidth || 80,
    });

    try {
      return await this.printRaw(printerName, escposData);
    } catch (err) {
      return { success: false, error: err.message, canFallback: true };
    }
  }

  /**
   * Send test print to verify printer
   */
  async testPrint(printerName) {
    if (!printerName) {
      throw new Error('Nama printer tidak ditentukan');
    }

    if (!qz.websocket.isActive()) {
      throw new Error('QZ Tray tidak aktif');
    }

    const escposData = buildTestPrint(printerName);
    return this.printRaw(printerName, escposData);
  }

  /**
   * Open cash drawer
   */
  async openDrawer(printerName) {
    const printer = printerName || this.printerConfig.receipt;
    
    if (!printer || !qz.websocket.isActive()) {
      throw new Error('Tidak dapat membuka laci kas');
    }

    // ESC/POS command to open drawer
    const drawerCommand = ['\x1B\x70\x00\x19\xFA'];
    return this.printRaw(printer, drawerCommand);
  }

  // ============ STATUS & LISTENERS ============

  /**
   * Get current connection status
   */
  getStatus() {
    return {
      connection: this.status,
      isConnected: this.status === QZStatus.CONNECTED,
      isActive: qz.websocket.isActive(),
      error: this.lastError,
      printers: this.printers,
      config: this.printerConfig,
    };
  }

  /**
   * Check if QZ Tray is available and ready
   */
  isReady() {
    return this.status === QZStatus.CONNECTED && qz.websocket.isActive();
  }

  /**
   * Check if receipt printing is available
   */
  canPrintReceipt() {
    return this.isReady() && !!this.printerConfig.receipt;
  }

  /**
   * Add status change listener
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Update status and notify listeners
   */
  _updateStatus(newStatus) {
    const oldStatus = this.status;
    this.status = newStatus;
    
    if (oldStatus !== newStatus) {
      this._notifyListeners();
    }
  }

  /**
   * Notify all listeners of status change
   */
  _notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (err) {
        console.error('[QZ] Listener error:', err);
      }
    });
  }

  /**
   * Parse QZ error to user-friendly message
   */
  _parseError(err) {
    if (!err) return 'Unknown error';
    
    const message = err.message || err.toString();
    
    if (message.includes('Unable to establish')) {
      return 'QZ Tray tidak berjalan. Silakan start QZ Tray.';
    }
    if (message.includes('Connection closed')) {
      return 'Koneksi ke QZ Tray terputus.';
    }
    if (message.includes('not found')) {
      return 'Printer tidak ditemukan.';
    }
    if (message.includes('offline')) {
      return 'Printer sedang offline.';
    }
    
    return message;
  }
}

// Singleton instance
const qzPrintService = new QZPrintService();

export default qzPrintService;
export { QZPrintService };
